import { DateFormatter, DueDateFormatter, Translate } from '@components'

import { InternalAccessor, TableColumn } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SimpleCegidIntegrationStatus } from '@features/cegidIntegration/elements/CegidIntegrationStatus'
import { CegidIntegrationStatusHeader } from '@features/cegidIntegration/elements/CegidIntegrationStatusHeader'
import { useIsPdpIntegrationActive } from '@features/invoices/hooks/eInvoices/useIsPdpIntegrationActive'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'

import { useInvoicesList } from '../../../context/invoicesList'
import { useInvoicesListFilters } from '../../../context/invoicesListFilters'
import { InvoicesTableAccessor } from '../../../enums/invoicesTableAccessor'
import type { InvoicesTableRow } from '../../../types/invoicesTableRow'
import { EInvoiceStatusCell } from '../elements/EInvoiceStatusCell'
import { InvoiceAmountCell } from '../elements/InvoiceAmountCell'
import { InvoiceDateCell } from '../elements/InvoiceDateCell'
import { InvoiceDescription } from '../elements/InvoiceDescription'
import { InvoicesTableStaticActions } from '../elements/InvoicesTableStaticActions'
import { InvoiceStatus } from '../elements/InvoiceStatus'
import { getFilteredInvoicesTableColumns } from '../utils/getFilteredInvoicesTableColumns'
import { getInvoicesTableColumnCell } from '../utils/getInvoicesTableColumnCell'

export const useInvoicesTableColumns = () => {
  const { t } = useTranslation()
  const { columns: activeColumns, organization } = useInvoicesList()
  const [{ status }] = useInvoicesListFilters()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { isPdpIntegrationActive } = useIsPdpIntegrationActive({ enabled: isFrenchComplianceMode })

  const isCegidOrganization = !!organization?.isCegid

  const columns: TableColumn<InvoicesTableRow>[] = useMemo(() => {
    const filteredActiveColumns = getFilteredInvoicesTableColumns({
      columns: activeColumns,
      isCegidOrganization,
      isFrenchComplianceMode,
      isPdpIntegrationActive,
      status,
    })
    const mappedColumns: TableColumn<InvoicesTableRow>[] = filteredActiveColumns.map((column) => {
      switch (column) {
        case InvoicesTableAccessor.Status:
          return {
            accessor: InvoicesTableAccessor.Status,
            alignment: 'left',
            Cell: ({ row, value: status }) => (
              <InvoiceStatus status={status} deliveryStatus={row.original.deliveryStatus} />
            ),
            renderAsChildren: true,
            size: 'xxxs',
            sortable: false,
          }
        case InvoicesTableAccessor.Type:
          return {
            accessor: InvoicesTableAccessor.Type,
            Cell: getInvoicesTableColumnCell,
            Header: <Translate value="invoices.table.column.document_type" />,
            size: 's',
            sortable: false,
            truncate: true,
          }
        case InvoicesTableAccessor.InvoiceNumber:
          return {
            accessor: InvoicesTableAccessor.InvoiceNumber,
            Cell: getInvoicesTableColumnCell,
            Header: <Translate value="invoices.table.column.invoice_no" />,
            size: 'xs',
            sortable: true,
            truncate: true,
          }
        case InvoicesTableAccessor.IssueDate:
          return {
            accessor: InvoicesTableAccessor.IssueDate,
            Cell: ({ row, value }) => <InvoiceDateCell date={value} isVoided={row.original.isVoided} />,
            fixedSize: true,
            Header: <Translate value="invoices.table.column.date" />,
            size: 's',
            sortable: true,
          }
        case InvoicesTableAccessor.DueDate:
          return {
            accessor: InvoicesTableAccessor.DueDate,
            Cell: ({ value: dueDate, row, ...rest }) => {
              const shouldIgnoreOverdueHighlight = row.original.isVoided || row.original.isPaid

              return getInvoicesTableColumnCell({
                ...rest,
                row,
                value: shouldIgnoreOverdueHighlight ? (
                  <DateFormatter value={dueDate} />
                ) : (
                  <DueDateFormatter value={dueDate} />
                ),
              })
            },
            fixedSize: true,
            Header: <Translate value="invoices.table.column.due_date" />,
            size: 's',
            sortable: true,
          }
        case InvoicesTableAccessor.PaidDate:
          return {
            accessor: InvoicesTableAccessor.PaidDate,
            Cell: ({ row, value }) => <InvoiceDateCell date={value} isVoided={row.original.isVoided} />,
            fixedSize: true,
            Header: <Translate value="invoices.table.column.paid_date" />,
            hidden: true,
            isNewColumn: true,
            size: 's',
            sortable: false,
          }
        case InvoicesTableAccessor.ContactName:
          return {
            accessor: (accessors) => accessors['contact.name'],
            Cell: getInvoicesTableColumnCell,
            Header: <Translate value="invoices.table.column.customer" />,
            id: 'contact.name',
            size: 's',
            sortable: true,
            truncate: true,
          }
        case InvoicesTableAccessor.EInvoiceStatus:
          return {
            accessor: InvoicesTableAccessor.EInvoiceStatus,
            alignment: 'left',
            Cell: ({ value: eInvoiceStatus }) => <EInvoiceStatusCell eInvoiceStatus={eInvoiceStatus} />,
            Header: <Translate value="invoices.table.column.einvoice_status" />,
            size: 's',
            sortable: false,
            truncate: true,
          }
        case InvoicesTableAccessor.Description:
          return {
            accessor: InvoicesTableAccessor.Description,
            Cell: ({ row, value: descriptionData }) => {
              const { isVoided } = row.original

              return descriptionData ? (
                <InvoiceDescription isFaded={isVoided} title={descriptionData.value}>
                  {descriptionData.value}
                </InvoiceDescription>
              ) : null
            },
            Header: <Translate value="invoices.table.column.description" />,
            size: 'm',
            truncate: true,
          }
        case InvoicesTableAccessor.IntegrationStatus:
          return {
            accessor: InvoicesTableAccessor.IntegrationStatus,
            alignment: 'center',
            Cell: ({ value: integrationStatus }) => {
              return integrationStatus ? <SimpleCegidIntegrationStatus status={integrationStatus} /> : null
            },
            Header: <CegidIntegrationStatusHeader tooltip={t('cegid_integration_status.table.invoice.tooltip')} />,
            hidden: !isCegidOrganization,
            size: 'xxs',
          }
        case InvoicesTableAccessor.NetTotal:
          return {
            accessor: InvoicesTableAccessor.NetTotal,
            alignment: 'right',
            Cell: ({ row, value }) => <InvoiceAmountCell amount={value} isVoided={row.original.isVoided} />,
            Header: <Translate value="invoices.table.column.excl_vat" />,
            size: 's',
            sortable: true,
          }
        case InvoicesTableAccessor.InclVat:
          return {
            accessor: InvoicesTableAccessor.InclVat,
            alignment: 'right',
            Cell: ({ row, value }) => <InvoiceAmountCell amount={value} isVoided={row.original.isVoided} />,
            Header: <Translate value="invoices.table.column.incl_vat" />,
            hidden: true,
            isNewColumn: true,
            size: 's',
            sortable: false,
          }
        default:
          throw new Error(`Unsupported column: ${column}`)
      }
    })

    const staticActionsColumn: TableColumn<InvoicesTableRow> = {
      accessor: InternalAccessor.StaticActions,
      Cell: ({ row }) => {
        const {
          billingStage,
          documentLocale,
          documentType,
          id,
          staticActionsItems,
          [InvoicesTableAccessor.InvoiceNumber]: invoiceNumber,
        } = row.original

        return (
          <InvoicesTableStaticActions
            billingStage={billingStage}
            documentLocale={documentLocale}
            documentType={documentType}
            invoiceNumber={invoiceNumber}
            items={staticActionsItems}
            invoiceId={id}
          />
        )
      },
      Header: '',
    }

    return [...mappedColumns, staticActionsColumn]
  }, [activeColumns, isCegidOrganization, isFrenchComplianceMode, isPdpIntegrationActive, status, t])

  return {
    columns,
  }
}
